/**
 * EmployeeSidebar Component
 * 
 * Navigation sidebar component for employees.
 * Provides navigation between payslip and settings pages.
 * 
 * Features:
 * - Collapsible sidebar functionality
 * - Different behavior for mobile vs desktop
 * - Visual indicators for active page
 * - Toggle button to collapse/expand sidebar
 * 
 * @component
 */

import React, { useState } from 'react';
import './EmployeeSidebar.css';

/**
 * Props interface for EmployeeSidebar component
 */
interface EmployeeSidebarProps {
  activePage: string;                    // Currently active page identifier
  setActivePage: (page: string) => void; // Callback to change active page
  isCollapsed: boolean;                  // Whether sidebar is collapsed
  toggleSidebar: () => void;             // Callback to toggle sidebar collapse state
  isMobile?: boolean;                    // Whether component is in mobile view
}

export const EmployeeSidebar: React.FC<EmployeeSidebarProps> = ({
  activePage,
  setActivePage,
  isCollapsed,
  toggleSidebar,
  isMobile = false
}) => {
  /**
   * Navigation items for employee view.
   * Each item has an id (for routing), label (display text), and icon (emoji).
   */
  const navItems = [
    { id: 'payslip', label: '給与明細', icon: '💰' },
    { id: 'documents', label: '書類', icon: '📄' },
    { id: 'settings', label: '設定', icon: '⚙️' }
  ];

  /**
   * Returns the appropriate toggle icon based on device type and collapse state.
   * Mobile: hamburger menu (☰) when collapsed, close (✕) when expanded
   * Desktop: right arrow (→) when collapsed, left arrow (←) when expanded
   * 
   * @returns String representing the toggle icon
   */
  const getToggleIcon = () => {
    if (isMobile) {
      return isCollapsed ? '☰' : '✕';
    }
    return isCollapsed ? '→' : '←';
  };

  return (
    <div className={`employee-sidebar ${isCollapsed ? 'collapsed' : ''} ${isMobile ? 'mobile' : ''}`}>
      {/* Sidebar header section containing title and toggle button */}
      <div className="sidebar-header">
        <div className="header-content">
          {/* Hide title on desktop when collapsed, always show on mobile */}
          <h3 className={isCollapsed && !isMobile ? 'hidden' : ''}>給与明細システム</h3>
        </div>
        
        {/* Toggle button for collapsing/expanding sidebar */}
        {/* Tooltip text changes based on device type and current state */}
        <button 
          className="sidebar-toggle"
          onClick={toggleSidebar}
          title={isMobile 
            ? (isCollapsed ? 'Open navigation menu' : 'Close navigation menu')
            : (isCollapsed ? 'Expand sidebar' : 'Collapse sidebar')
          }
        >
          {getToggleIcon()}
        </button>
      </div>
      
      {/* Main navigation menu with list of navigation items */}
      <nav className="sidebar-nav">
        <ul>
          {navItems.map((item) => (
            <li key={item.id}>
              {/* Navigation button with active state highlighting */}
              {/* Shows tooltip on desktop when collapsed */}
              <button 
                className={`nav-item ${activePage === item.id ? 'active' : ''}`}
                onClick={() => setActivePage(item.id)}
                title={isCollapsed && !isMobile ? item.label : ''}
              >
                <span className="nav-icon">{item.icon}</span>
                {/* Hide label text on desktop when collapsed, always show on mobile */}
                <span className={`nav-label ${isCollapsed && !isMobile ? 'hidden' : ''}`}>
                  {item.label}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </nav>
      
      {/* Sidebar footer section with company copyright information */}
      {/* Hide footer text on desktop when collapsed, always show on mobile */}
      <div className="sidebar-footer">
        <p className={isCollapsed && !isMobile ? 'hidden' : ''}>
          © 2026 株式会社西行土木
        </p>
      </div>
    </div>
  );
};
